import type { DiscontinuationWorkflowModalMode } from "@/components/ActiveProjects/shared/components/config/discontinuationWorkflowModalConfig";

type PackagingAndLabellingLabels = {
  DISCONTINUATION?: {
    NO_BUYER_ASSIGNED_BANNER?: string;
  };
  DISCONTINUATION_MODAL?: {
    COMMON?: {
      CANCEL?: string;
      CONFIRM?: string;
    };
    TITLES?: Partial<Record<string, string>>;
    WARNINGS?: {
      COMPLETE_DISCONTINUATION?: string;
    };
    CANCEL_PROJECT_FIELDS?: {
      REASON_FOR_CANCELING?: string;
      NOTES_FOR_CANCELING?: string;
    };
    CANCEL_PROJECT_INFORMATION?: {
      TITLE?: string;
      PROJECT_CANCELLED_DATE?: string;
      REASON_FOR_CANCELING?: string;
      NOTES_FOR_CANCELING?: string;
    };
    CANCEL_DISCONTINUATION_REQUEST?: {
      NOTES_LABEL?: string;
      CANCEL_LABEL?: string;
      SUBMIT_LABEL?: string;
    };
    FOOTER?: Partial<
      Record<
        string,
        {
          CANCEL?: string;
          SUBMIT?: string;
        }
      >
    >;
  };
};

const NO_BUYER_ASSIGNED_BANNER_FALLBACK =
  "No Buyer is assigned to this product. Please assign a Buyer to proceed with discontinuation.";

const MODAL_TITLE_FALLBACKS: Record<DiscontinuationWorkflowModalMode, string> = {
  "discontinue-product": "Discontinue Product",
  "cancel-discontinuation-request": "Cancel Discontinuation Request",
  "complete-discontinuation": "Complete Discontinuation",
  "cancel-project": "Cancel Project",
};

const MODAL_TITLE_KEYS: Record<DiscontinuationWorkflowModalMode, string> = {
  "discontinue-product": "DISCONTINUE_PRODUCT",
  "cancel-discontinuation-request": "CANCEL_DISCONTINUATION_REQUEST",
  "complete-discontinuation": "COMPLETE_DISCONTINUATION",
  "cancel-project": "CANCEL_PROJECT",
};

const FOOTER_FALLBACKS: Record<
  DiscontinuationWorkflowModalMode,
  { cancelLabel: string; submitLabel: string; submitPrimary?: boolean }
> = {
  "discontinue-product": {
    cancelLabel: "CANCEL",
    submitLabel: "INITIATE DISCONTINUATION",
    submitPrimary: true,
  },
  "cancel-discontinuation-request": {
    cancelLabel: "CANCEL",
    submitLabel: "CANCEL REQUEST",
    submitPrimary: true,
  },
  "complete-discontinuation": {
    cancelLabel: "CANCEL",
    submitLabel: "CONFIRM",
    submitPrimary: true,
  },
  "cancel-project": {
    cancelLabel: "CANCEL",
    submitLabel: "CONFIRM",
    submitPrimary: true,
  },
};

const FOOTER_KEYS: Record<DiscontinuationWorkflowModalMode, string> = {
  "discontinue-product": "DISCONTINUE_PRODUCT",
  "cancel-discontinuation-request": "CANCEL_DISCONTINUATION_REQUEST",
  "complete-discontinuation": "COMPLETE_DISCONTINUATION",
  "cancel-project": "CANCEL_PROJECT",
};

const COMPLETE_DISCONTINUATION_WARNING_FALLBACK =
  "Confirming discontinuation will affect with active version, superseded, in-flight, revision versions";

const CANCEL_PROJECT_FIELD_LABELS_FALLBACK = {
  reasonForDiscontinuation: "Reason For Canceling",
  notesForDiscontinuation: "Notes For Canceling",
};

const CANCEL_PROJECT_INFORMATION_SECTION_FALLBACK = {
  title: "Cancel Project Information",
  discontinuationDate: "Project Canceled Date",
  reasonForDiscontinuation: "Reason For Canceling",
  notesForDiscontinuation: "Notes For Canceling",
};

const CANCEL_DISCONTINUATION_REQUEST_COPY_FALLBACK = {
  notesLabel: "Notes For Cancel Request",
  cancelLabel: "CLOSE",
  submitLabel: "CANCEL REQUEST",
};

export function resolveDiscontinuationNoBuyerAssignedBannerMessage(
  packagingLabels?: PackagingAndLabellingLabels | null
): string {
  const fromLocale = String(
    packagingLabels?.DISCONTINUATION?.NO_BUYER_ASSIGNED_BANNER ?? ""
  ).trim();
  return fromLocale || NO_BUYER_ASSIGNED_BANNER_FALLBACK;
}

export function resolveDiscontinuationModalTitle(
  mode: DiscontinuationWorkflowModalMode,
  packagingLabels?: PackagingAndLabellingLabels | null
): string {
  const key = MODAL_TITLE_KEYS[mode];
  const fromLocale = String(packagingLabels?.DISCONTINUATION_MODAL?.TITLES?.[key] ?? "").trim();
  return fromLocale || MODAL_TITLE_FALLBACKS[mode];
}

const MODES_USING_COMMON_CONFIRM: DiscontinuationWorkflowModalMode[] = [
  "complete-discontinuation",
  "cancel-project",
];

export function resolveDiscontinuationModalFooterLabels(
  mode: DiscontinuationWorkflowModalMode,
  packagingLabels?: PackagingAndLabellingLabels | null
): { cancelLabel: string; submitLabel: string; submitPrimary?: boolean } {
  const fallback = FOOTER_FALLBACKS[mode];
  const key = FOOTER_KEYS[mode];
  const modalLabels = packagingLabels?.DISCONTINUATION_MODAL;
  const fromLocale = modalLabels?.FOOTER?.[key];
  const common = modalLabels?.COMMON;

  const cancelLabel =
    String(fromLocale?.CANCEL ?? "").trim() ||
    String(common?.CANCEL ?? "").trim() ||
    fallback.cancelLabel;

  const submitLabel =
    String(fromLocale?.SUBMIT ?? "").trim() ||
    (MODES_USING_COMMON_CONFIRM.includes(mode)
      ? String(common?.CONFIRM ?? "").trim()
      : "") ||
    fallback.submitLabel;

  return {
    cancelLabel,
    submitLabel,
    submitPrimary: fallback.submitPrimary,
  };
}

export function resolveCompleteDiscontinuationWarningMessage(
  packagingLabels?: PackagingAndLabellingLabels | null
): string {
  const fromLocale = String(
    packagingLabels?.DISCONTINUATION_MODAL?.WARNINGS?.COMPLETE_DISCONTINUATION ?? ""
  ).trim();
  return fromLocale || COMPLETE_DISCONTINUATION_WARNING_FALLBACK;
}

export function resolveCancelProjectModalFieldLabels(
  packagingLabels?: PackagingAndLabellingLabels | null
): {
  reasonForDiscontinuation: string;
  notesForDiscontinuation: string;
} {
  const fromLocale = packagingLabels?.DISCONTINUATION_MODAL?.CANCEL_PROJECT_FIELDS;
  return {
    reasonForDiscontinuation:
      String(fromLocale?.REASON_FOR_CANCELING ?? "").trim() ||
      CANCEL_PROJECT_FIELD_LABELS_FALLBACK.reasonForDiscontinuation,
    notesForDiscontinuation:
      String(fromLocale?.NOTES_FOR_CANCELING ?? "").trim() ||
      CANCEL_PROJECT_FIELD_LABELS_FALLBACK.notesForDiscontinuation,
  };
}

export function resolveCancelProjectInformationSectionLabels(
  packagingLabels?: PackagingAndLabellingLabels | null
): {
  title: string;
  discontinuationDate: string;
  reasonForDiscontinuation: string;
  notesForDiscontinuation: string;
} {
  const fromLocale = packagingLabels?.DISCONTINUATION_MODAL?.CANCEL_PROJECT_INFORMATION;
  const modalFields = packagingLabels?.DISCONTINUATION_MODAL?.CANCEL_PROJECT_FIELDS;
  return {
    title:
      String(fromLocale?.TITLE ?? "").trim() ||
      CANCEL_PROJECT_INFORMATION_SECTION_FALLBACK.title,
    discontinuationDate:
      String(fromLocale?.PROJECT_CANCELLED_DATE ?? "").trim() ||
      CANCEL_PROJECT_INFORMATION_SECTION_FALLBACK.discontinuationDate,
    reasonForDiscontinuation:
      String(fromLocale?.REASON_FOR_CANCELING ?? modalFields?.REASON_FOR_CANCELING ?? "").trim() ||
      CANCEL_PROJECT_INFORMATION_SECTION_FALLBACK.reasonForDiscontinuation,
    notesForDiscontinuation:
      String(fromLocale?.NOTES_FOR_CANCELING ?? modalFields?.NOTES_FOR_CANCELING ?? "").trim() ||
      CANCEL_PROJECT_INFORMATION_SECTION_FALLBACK.notesForDiscontinuation,
  };
}

export function resolveCancelDiscontinuationRequestModalCopy(
  packagingLabels?: PackagingAndLabellingLabels | null
): {
  notesLabel: string;
  cancelLabel: string;
  submitLabel: string;
} {
  const fromLocale = packagingLabels?.DISCONTINUATION_MODAL?.CANCEL_DISCONTINUATION_REQUEST;
  return {
    notesLabel:
      String(fromLocale?.NOTES_LABEL ?? "").trim() ||
      CANCEL_DISCONTINUATION_REQUEST_COPY_FALLBACK.notesLabel,
    cancelLabel:
      String(fromLocale?.CANCEL_LABEL ?? "").trim() ||
      CANCEL_DISCONTINUATION_REQUEST_COPY_FALLBACK.cancelLabel,
    submitLabel:
      String(fromLocale?.SUBMIT_LABEL ?? "").trim() ||
      CANCEL_DISCONTINUATION_REQUEST_COPY_FALLBACK.submitLabel,
  };
}
